// Module-level cache for the SDK's AiService instance.
//
// The SDK exposes services through getService(name) which is wrapped in
// an async init-await Proxy (see ../governanceBridge.js → sdk.js
// wrapWithInitAwait), so synchronous DOMQL factories (the ParamsBtn
// picker, AppAssistant message rendering, ticket AI surfaces) can't
// touch it directly.
//
// Storing the resolved instance on root state breaks too: smbls' deep
// state clone strips the class prototype, leaving plain own properties
// behind — `_aiSvc.getMode()` then throws "not a function". A module
// singleton bypasses both problems: callers grab the live class
// instance synchronously through `getAiService()` once it's been
// resolved at shell boot.
//
// Boot path (workspace/app.js shellApp.onCreate):
//   import { resolveAiService } from '@symbo-ls/workspace-shared/functions/aiService.js'
//   resolveAiService(el.sdk)
//
// Consumer path (anywhere):
//   import { getAiService } from '…/aiService.js'
//   const ai = getAiService()
//   if (ai) ai.dispatch(...)
//
// `bumpTick(root)` exists so picker UIs can nudge a state-level signal
// after changing mode — DOMQL factories that read sdk.ai.getMode() can
// then re-evaluate. Without a state-side hook the dropdown would render
// stale until reopened.

import { getSDK } from './sdk.js'

// FA204 / frank trap — `_aiSvc`/`_resolvePromise` used to live at module
// scope, referenced bare across every export below. Unresolvable once each
// export is individually frank.toJSON'd for platform delivery
// (project_frank_drops_module_local_helpers). Both are GENUINE cross-call
// state by design (see the file header above — a module singleton exists
// specifically because storing the resolved instance on root state strips
// its class prototype), with no el/root argument anywhere in this file to
// hoist them onto instead — re-homed onto `globalThis.__aiServiceState`,
// the same argument-less escape hatch as pointerCapability.js's
// `window.__pointerCapabilityState` / mwtFlag.js's `globalThis.__mwtFlagCache`.

// Kick off resolution. Returns a Promise that resolves once
// `sdk.getService('ai')` completes. Idempotent — second call returns
// the existing promise.
export function resolveAiService(sdk) {
  const state = (globalThis.__aiServiceState = globalThis.__aiServiceState || {
    aiSvc: null,
    resolvePromise: null
  })
  if (state.aiSvc) return Promise.resolve(state.aiSvc)
  if (state.resolvePromise) return state.resolvePromise
  const real = sdk || getSDK()
  if (!real) return Promise.reject(new Error('[aiService] no SDK'))
  state.resolvePromise = Promise.resolve(real.getService('ai'))
    .then((svc) => {
      state.aiSvc = svc
      return svc
    })
    .catch((err) => {
      state.resolvePromise = null
      throw err
    })
  return state.resolvePromise
}

// Sync accessor. Returns the resolved AiService instance or null if
// resolveAiService hasn't completed yet. Callers in reactive factories
// guard on `if (!ai) return …` so the UI degrades gracefully during
// the boot window.
export function getAiService() {
  return globalThis.__aiServiceState ? globalThis.__aiServiceState.aiSvc : null
}

// Force-reset the cache. Used by tests + the auth-state subscriber on
// sign-out (the new sign-in might bring a fresh service binding).
export function _clearAiServiceCache() {
  globalThis.__aiServiceState = { aiSvc: null, resolvePromise: null }
}
